import AsyncStorage from '@react-native-async-storage/async-storage';

/**
 * Get the secure file URL for viewing attachments
 * @param {string} queueUserId - The queue user ID
 * @param {string} objectName - The file object name
 * @returns {string} - The secure API endpoint URL
 */
export const getSecureFileUrl = (queueUserId, objectName) => {
  // This function is kept for backward compatibility but now returns a relative path
  // that will be handled by RTK Query
  return `queue-user/${queueUserId}/attachment/${objectName}`;
};

/**
 * Extract object name from attachment URL
 * @param {string} fileUrl - The file URL
 * @returns {string|null} - The extracted object name
 */
export const getObjectNameFromUrl = (fileUrl) => {
  if (!fileUrl) return null;

  try {
    // For secure attachment URLs, extract the object name from the URL
    if (
      fileUrl.includes('/api/queue-user/') &&
      fileUrl.includes('/attachment/')
    ) {
      const urlParts = fileUrl.split('/');
      return urlParts[urlParts.length - 1];
    }

    // For legacy MinIO URLs (various formats)
    if (
      fileUrl.includes('queue-user-attachments') ||
      (fileUrl.includes('minio') && fileUrl.includes('/object/'))
    ) {
      // Handle presigned URL format: /queue-user-attachments/uuid/filename
      if (fileUrl.includes('queue-user-attachments')) {
        const parts = fileUrl.split('queue-user-attachments/')[1];
        if (parts) {
          // Extract filename from: uuid/filename?query or uuid/filename
          const pathParts = parts.split('?')[0].split('/');
          if (pathParts.length >= 2) {
            return pathParts[1]; // Just the filename part
          }
        }
      }

      // Handle direct MinIO URL format: /object/uuid/filename
      if (fileUrl.includes('/object/')) {
        const parts = fileUrl.split('/object/');
        if (parts.length > 1) {
          const pathParts = parts[1].split('/');
          if (pathParts.length >= 2) {
            return pathParts[1]; // Just the filename part
          }
          return parts[1]; // Fallback to full path
        }
      }
    }

    return null;
  } catch (error) {
    console.error('Error extracting object name:', error);
    return null;
  }
};

/**
 * Get authentication headers for secure API calls
 * @returns {Promise<Object>} - Headers object with Authorization
 */
export const getAuthHeaders = async () => {
  try {
    const token = await AsyncStorage.getItem('token');

    if (!token) {
      console.warn('getAuthHeaders: No token found in AsyncStorage');
      return {};
    }

    return {
      Authorization: `Bearer ${token}`,
      Accept: '*/*',
      'Content-Type': 'application/json',
    };
  } catch (error) {
    console.error('Error getting auth token:', error);
    return {};
  }
};
